/**
 * The linked-chip rail (P-L2).
 *
 * Shows what else is attached to the record you are looking at: the proposal a
 * pursuit spawned, the program it became, how many touchpoints it carries. Doc
 * 09's "UI altitudes" calls for HubSpot-style association chips on every record,
 * read from ONE resolver so no surface invents its own traversal.
 *
 * Deliberately NOT a pipeline. A chip says "this is attached", never "this
 * record has reached this stage" — a pursuit survives no-bid, park, loss, and
 * concurrent delivery, and one program can wear two sibling recompete pursuits.
 * Nothing here is draggable and nothing implies order.
 *
 * Renders nothing at all when a record has no links and no counts: an empty rail
 * on every page would be noise, and doc 09's readers are lazy by design.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { LifecycleNodeKind, useLinkedNodes } from './useLinkedNodes';
/** Human labels. The proto carries machine keys; naming is the frontend's job. */ const KIND_LABEL = {
    [LifecycleNodeKind.PURSUIT]: 'Pursuit',
    [LifecycleNodeKind.PROPOSAL]: 'Proposal',
    [LifecycleNodeKind.PRE_AWARD]: 'Pre-award',
    [LifecycleNodeKind.PROGRAM]: 'Program',
    [LifecycleNodeKind.RFI_RESPONSE]: 'RFI response',
    [LifecycleNodeKind.OPPORTUNITY]: 'Opportunity',
    [LifecycleNodeKind.NOTICE]: 'Notice'
};
const COUNT_LABEL = {
    go_no_go: 'Go/no-go',
    rfi_responses: 'RFI responses',
    touchpoints: 'Touchpoints',
    workstreams: 'Workstreams',
    proposal_milestones: 'Milestones',
    teaming: 'Teaming',
    competition: 'Competition'
};
/**
 * Routes live here, not in the gateway — a data-plane service minting URLs would
 * put routing in two places. Returns undefined for kinds with no route yet, and
 * those chips render unlinked rather than as a dead link.
 */ function routeFor(kind, id, zouProjectId) {
    switch(kind){
        case LifecycleNodeKind.PURSUIT:
            return `/capture/${id}`;
        case LifecycleNodeKind.PROPOSAL:
            // /proposal/:id, not /projects/:id — the latter is not a declared route,
            // and ConvertToProposalDialog navigates here too.
            return `/proposal/${zouProjectId || id}`;
        case LifecycleNodeKind.PRE_AWARD:
            return `/delivery/pre-award/${id}`;
        case LifecycleNodeKind.PROGRAM:
            return `/delivery/programs/${id}`;
        case LifecycleNodeKind.RFI_RESPONSE:
            return `/rfi-response/${zouProjectId || id}`;
        case LifecycleNodeKind.OPPORTUNITY:
        case LifecycleNodeKind.NOTICE:
            return '/opportunities';
        default:
            return undefined;
    }
}
function edgeLabel(edge) {
    const kind = KIND_LABEL[edge.node?.kind ?? 0] ?? 'Linked record';
    const name = edge.node?.label?.trim();
    // Pangaea holds the links but zou still owns project names, so a proposal chip
    // can legitimately arrive unlabelled. Show the kind rather than an id.
    return name ? `${kind}: ${name}` : kind;
}
/** One neighbour chip, linked when its kind has a route. */ function EdgeChip({ edge }) {
    const node = edge.node;
    if (!node) return null;
    const to = routeFor(node.kind, node.id, node.zouProjectId);
    const chip = /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: "info",
        "data-testid": Testids.lifecycleChips.chip,
        "data-node-kind": node.kind,
        "data-node-id": node.id,
        children: edgeLabel(edge)
    });
    // Unrouted kinds render as plain chips rather than dead links.
    return to ? /*#__PURE__*/ _jsx(Link, {
        to: to,
        className: "no-underline",
        children: chip
    }) : chip;
}
export function LinkedChips({ kind, id, className }) {
    const { data, isLoading, isError } = useLinkedNodes(kind, id);
    // Silent while loading and on error. A chip rail is ambient context, not the
    // page's job — a spinner or a red banner here would be louder than the thing
    // it decorates.
    if (isLoading || isError || !data) return null;
    const edges = data.edges ?? [];
    const counts = (data.counts ?? []).filter((c)=>c.count > 0);
    const unresolved = data.unresolved ?? [];
    if (edges.length === 0 && counts.length === 0 && unresolved.length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('flex flex-wrap items-center gap-[var(--space-2)]', className),
        "data-testid": Testids.lifecycleChips.rail,
        children: [
            edges.map((edge)=>edge.node ? /*#__PURE__*/ _jsx(EdgeChip, {
                    edge: edge
                }, `${edge.node.kind}-${edge.node.id}`) : null),
            counts.map((count)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "neutral",
                    "data-testid": Testids.lifecycleChips.count,
                    "data-count-key": count.key,
                    children: `${COUNT_LABEL[count.key] ?? count.key} (${count.count})`
                }, count.key)),
            unresolved.length > 0 && // Provider is scoped here, not assumed from an ancestor: this rail
            // mounts on capture, proposal and delivery pages, and there is no
            // app-root TooltipProvider to inherit. Radix tolerates nesting, so a
            // page that has its own provider is unaffected.
            /*#__PURE__*/ _jsx(TooltipProvider, {
                children: /*#__PURE__*/ _jsxs(Tooltip, {
                    children: [
                        /*#__PURE__*/ _jsx(TooltipTrigger, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsxs(SpecraBadge, {
                                variant: "warning",
                                "data-testid": Testids.lifecycleChips.unresolved,
                                children: [
                                    /*#__PURE__*/ _jsx(AlertTriangle, {
                                        className: "size-3",
                                        "aria-hidden": true
                                    }),
                                    `${unresolved.length} unresolved`
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(TooltipContent, {
                            children: /*#__PURE__*/ _jsxs("p", {
                                className: "max-w-xs",
                                children: [
                                    unresolved.length === 1 ? 'A link points' : 'Links point',
                                    " at a record that no longer exists. The data is fine to use — the missing piece just cannot be shown."
                                ]
                            })
                        })
                    ]
                })
            })
        ]
    });
}
